
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Product } from '../types';
import Toast from '../components/Toast';

export interface CartItem {
    productId: number;
    name: string;
    nameEn: string;
    price: string;
    priceValue: number;
    image: string;
    quantity: number;
}

interface CartContextType {
    items: CartItem[];
    itemCount: number;
    total: number;
    totalFormatted: string;
    addToCart: (product: Product, quantity?: number) => void;
    removeFromCart: (productId: number) => void;
    updateQuantity: (productId: number, quantity: number) => void;
    clearCart: () => void;
    isCartOpen: boolean;
    setCartOpen: (open: boolean) => void;
    getCartSummary: (lang: 'vi' | 'en') => string;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'squeaky_cart';

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isCartOpen, setCartOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    // Load cart from localStorage on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(CART_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    setItems(parsed);
                }
            }
        } catch (e) {
            console.error('Error loading cart:', e);
        }
    }, []);

    // Save cart to localStorage whenever items change
    useEffect(() => {
        try {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
        } catch (e) {
            console.error('Error saving cart:', e);
        }
    }, [items]);

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const total = items.reduce((sum, item) => sum + (item.priceValue * item.quantity), 0);

    const totalFormatted = new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
    }).format(total);

    const addToCart = useCallback((product: Product, quantity = 1) => {
        setItems(prev => {
            const existing = prev.find(item => item.productId === product.id);
            if (existing) {
                return prev.map(item =>
                    item.productId === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, {
                productId: product.id,
                name: product.name,
                nameEn: product.nameEn,
                price: product.price,
                priceValue: product.priceValue,
                image: product.image,
                quantity
            }];
        });

        // CẬP NHẬT: Không mở giỏ hàng nữa, chỉ hiện thông báo
        // setCartOpen(true); 
        setToastMessage(`Đã thêm ${product.name} vào giỏ!`);
        setShowToast(true);
    }, []);

    const removeFromCart = useCallback((productId: number) => {
        setItems(prev => prev.filter(item => item.productId !== productId));
    }, []);

    const updateQuantity = useCallback((productId: number, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(productId);
            return;
        }
        setItems(prev =>
            prev.map(item =>
                item.productId === productId ? { ...item, quantity } : item
            )
        );
    }, [removeFromCart]);

    const clearCart = useCallback(() => {
        setItems([]);
    }, []);

    const getCartSummary = useCallback((lang: 'vi' | 'en') => {
        if (items.length === 0) return '';

        const itemsList = items.map(item => {
            const name = lang === 'vi' ? item.name : item.nameEn;
            return `${name} x${item.quantity}`;
        }).join(', ');

        return `${lang === 'vi' ? 'Đơn hàng' : 'Order'}: ${itemsList}`;
    }, [items]);

    return (
        <CartContext.Provider value={{
            items,
            itemCount,
            total,
            totalFormatted,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            isCartOpen,
            setCartOpen,
            getCartSummary
        }}>
            {children}
            <Toast
                message={toastMessage}
                isVisible={showToast}
                onClose={() => setShowToast(false)}
            />
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
